import { post } from "@/services/axiosUitils";

export interface BarcodeProduct {
  product_name?: string;
  name?: string;
  brands?: string;
  brand?: string;
  image_url?: string;
  image?: string;
  calorie_content_kcal?: number;
  calories?: number;
  carbohydrate_content_g?: number;
  carbs?: number;
  sugar_g?: number;
  sugar?: number;
  fat_content_g?: number;
  fat?: number;
  protein_content_g?: number;
  protein?: number;
  dietary_fiber_g?: number;
  fiber?: number;
  saturated_fat_g?: number;
  saturated_fat?: number;
  trans_fat_g?: number;
  trans_fat?: number;
  sodium_mg?: number;
  sodium?: number;
  cholesterol_mg?: number;
  cholesterol?: number;
  serving_size_g?: number | string;
  serving_size?: string;
  serving_description?: string;
  ingredients_text?: string;
  ingredients?: string;
  GI_estimate?: number;
  gi_estimate?: number;
  GL_estimate?: number;
  gl_estimate?: number;
  net_carbs_g?: number;
  net_carbs?: number;
}

export interface BarcodeApiResponse {
  // Flat response fields (actual API format)
  success?: { code?: string; message?: string } | boolean;
  barcode?: string;
  date?: string;
  product_name?: string;
  generic_name?: string;
  quantity?: string;
  brand?: string;
  calories?: number;
  protein?: number;
  carbohydrates?: number;  // API uses "carbohydrates" not "carbs"
  sugar?: number;
  fat?: number;
  saturated_fat?: number;
  trans_fat?: number;
  fiber?: number;
  sodium?: number;
  cholesterol?: number;
  alcohol?: number;
  caffeine?: number;
  image_url?: string;
  ingredients?: string;
  meal_type?: string;
  other_details?: {
    meal_type?: string;
    [key: string]: unknown;
  };
  // Nested/array response fallbacks
  food_items?: BarcodeProduct[];
  results?: BarcodeProduct[];
  data?: any;
  error?: string;
}

export interface SimplifiedProduct {
  barcode: string;
  name: string;
  brand: string;
  image: string;
  calories: number;
  sugar: number;
  fat: number;
  protein: number;
  carbs: number;
  net_carbs: number;
  fiber: number;
  saturated_fat: number;
  trans_fat: number;
  sodium: number;
  cholesterol: number;
  serving_size: string;
  ingredients: string;
  nutrition_grade: string;
  health_score: number;
  gi_estimate: number;
  gl_estimate: number;
  meal_type?: string;
}

class BarcodeApiService {
  private endpoint = "/food_scanner/analyze_food_image/";

  /**
   * Lookup product by barcode using Glucera Internal API
   */
  async lookupProduct(barcode: string): Promise<SimplifiedProduct> {
    try {
      // ✅ Per curl: POST with query params only, empty body
      // curl --data '' → no body needed, barcode goes in query params
      const url = `${this.endpoint}?testing=false&barcode=${barcode}`;

      const response = await post<BarcodeApiResponse>(url, {});

      // ✅ axiosUtils returns undefined on error (swallows errors internally)
      // So we must check for undefined/null response first
      if (response === undefined || response === null) {
        throw new Error("Barcode not found");
      }

      // ✅ Handle API-level errors returned in response body (e.g. {"error":"Barcode not found"})
      const responseAny = response as any;
      if (responseAny.error) {
        const errMsg = typeof responseAny.error === "string"
          ? responseAny.error
          : responseAny.error?.message || "Barcode not found";
        throw new Error(errMsg);
      }

      // ✅ FLAT response format (actual API):
      // { product_name, brand, calories, carbohydrates, quantity, ... }
      const isFlatResponse = !!response.product_name || !!response.brand;

      if (isFlatResponse) {
        // Flat response — fields are directly on the response object
        const flatProduct = response;
        const healthScore = this.calculateHealthScoreFromValues(
          flatProduct.sugar ?? 0,
          flatProduct.fat ?? 0,
          flatProduct.saturated_fat ?? 0
        );

        // Use image_url from internal API response
        const imageUrl = flatProduct.image_url || "";

        return {
          barcode,
          name: flatProduct.product_name || "Unknown Product",
          brand: flatProduct.brand || "Unknown Brand",
          image: imageUrl,
          calories: flatProduct.calories ?? 0,
          sugar: flatProduct.sugar ?? 0,
          fat: flatProduct.fat ?? 0,
          protein: flatProduct.protein ?? 0,
          carbs: flatProduct.carbohydrates ?? 0,   // API sends "carbohydrates"
          net_carbs: (flatProduct.carbohydrates ?? 0) - (flatProduct.fiber ?? 0),
          fiber: flatProduct.fiber ?? 0,
          saturated_fat: flatProduct.saturated_fat ?? 0,
          trans_fat: flatProduct.trans_fat ?? 0,
          sodium: flatProduct.sodium ?? 0,
          cholesterol: flatProduct.cholesterol ?? 0,
          serving_size: flatProduct.quantity?.trim() || "",
          ingredients: flatProduct.ingredients || "Not available",
          nutrition_grade: "unknown",
          health_score: healthScore,
          gi_estimate: 0,
          gl_estimate: 0,
          meal_type:
            flatProduct.meal_type ||
            flatProduct.other_details?.meal_type ||
            undefined,
        };
      }

      // Fallback: Nested food_items array format
      const foodItems = response.food_items || response.results || (response.data?.food_items) || [];

      if (foodItems.length === 0) {
        throw new Error("Product not found");
      }

      const product = foodItems[0];

      // Calculate health score (0-10) based on nutrients
      const healthScore = this.calculateHealthScore(product);

      // Simplify the response, handling both internal and OPEN FOOD FACTS style fields
      return {
        barcode,
        name: product.name || product.product_name || "Unknown Product",
        brand: product.brand || product.brands || "Unknown Brand",
        image: product.image || product.image_url || "",
        calories: product.calorie_content_kcal || product.calories || 0,
        sugar: product.sugar_g || product.sugar || 0,
        fat: product.fat_content_g || product.fat || 0,
        protein: product.protein_content_g || product.protein || 0,
        carbs: product.carbohydrate_content_g || product.carbs || 0,
        net_carbs: product.net_carbs_g || product.net_carbs || 0,
        fiber: product.dietary_fiber_g || product.fiber || 0,
        saturated_fat: product.saturated_fat_g || product.saturated_fat || 0,
        trans_fat: product.trans_fat_g || product.trans_fat || 0,
        sodium: product.sodium_mg || product.sodium || 0,
        cholesterol: product.cholesterol_mg || product.cholesterol || 0,
        serving_size: String(
          product.serving_size ||
            product.serving_description ||
            product.serving_size_g ||
            ""
        ).trim() || "",
        ingredients: product.ingredients || product.ingredients_text || "Not available",
        nutrition_grade: "unknown",
        health_score: healthScore,
        gi_estimate: product.GI_estimate || product.gi_estimate || 0,
        gl_estimate: product.GL_estimate || product.gl_estimate || 0,
        meal_type:
          (product as BarcodeProduct & { meal_type?: string }).meal_type ||
          (response as BarcodeApiResponse).meal_type ||
          (response as BarcodeApiResponse).other_details?.meal_type ||
          undefined,
      };
    } catch (error: any) {
      console.error("❌ Barcode API Error:", error);

      // ✅ Re-throw errors we already formatted (from body error check above)
      if (
        error.message === "Barcode not found" ||
        error.message === "Product not found" ||
        error.message?.toLowerCase().includes("not found")
      ) {
        throw new Error(error.message);
      }

      // Handle HTTP 404
      if (error.response?.status === 404) {
        throw new Error("Product not found in database. Please try another barcode.");
      }

      // Handle the server error specifically
      if (error.response?.data?.error?.code === "server_error") {
        throw new Error("Server error: " + (error.response?.data?.error?.message || "Internal server error."));
      }

      // Handle string error in response data
      if (typeof error.response?.data?.error === "string") {
        throw new Error(error.response.data.error);
      }

      throw new Error("Failed to fetch product details. Please try again.");
    }
  }

  /**
   * Calculate health score directly from raw values (for flat response format)
   */
  private calculateHealthScoreFromValues(sugar: number, fat: number, saturatedFat: number): number {
    let score = 10;
    if (sugar > 15) score -= 3;
    else if (sugar > 10) score -= 2;
    else if (sugar > 5) score -= 1;
    if (fat > 20) score -= 2;
    else if (fat > 10) score -= 1;
    if (saturatedFat > 10) score -= 2;
    else if (saturatedFat > 5) score -= 1;
    return Math.max(0, Math.min(10, score));
  }

  /**
   * Calculate health score based on nutrition data
   * Returns score out of 10
   */
  private calculateHealthScore(product: BarcodeProduct): number {
    // Fallback calculation based on sugar and fat
    let score = 10;
    const sugar = product.sugar_g || product.sugar || 0;
    const fat = product.fat_content_g || product.fat || 0;
    const saturatedFat = product.saturated_fat_g || product.saturated_fat || 0;

    // Deduct points for high sugar
    if (sugar > 15) score -= 3;
    else if (sugar > 10) score -= 2;
    else if (sugar > 5) score -= 1;

    // Deduct points for high fat
    if (fat > 20) score -= 2;
    else if (fat > 10) score -= 1;

    // Deduct points for high saturated fat
    if (saturatedFat > 10) score -= 2;
    else if (saturatedFat > 5) score -= 1;

    return Math.max(0, Math.min(10, score));
  }

  /**
   * Get health recommendation based on score
   */
  getHealthRecommendation(score: number): {
    label: string;
    color: string;
    message: string;
  } {
    if (score >= 8) {
      return {
        label: "Excellent",
        color: "green",
        message: "Great choice! This product has good nutritional value.",
      };
    }
    if (score >= 6) {
      return {
        label: "Good",
        color: "blue",
        message: "This product is generally healthy. Enjoy in moderation.",
      };
    }
    if (score >= 4) {
      return {
        label: "Moderate",
        color: "yellow",
        message: "Consume occasionally. Consider healthier alternatives.",
      };
    }
    return {
      label: "Poor",
      color: "red",
      message: "⚠️ High in sugar/fat. Not recommended for regular consumption.",
    };
  }
}

export const barcodeApiService = new BarcodeApiService();
export default barcodeApiService;
